import { AxiosError } from 'axios';
import { SignInCredentials, ValidateCredentials, RegisterCredentials } from '../../models';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchUser, registerUser, signInRequest, validateUser } from '../../services/auth.service';
import { updateUserStatus } from '../../services/profile.service';
import { User } from '../../types/user';
import { clearStorage } from '../../utils/storage';

import { api } from '../../services/api';
import { setAuthorizationHeader } from '../../services/interceptors';
import { createTokenCookies, getToken, removeTokenCookies } from '../../utils/tokenCookies';
import { RuleEntity } from 'types/rule';
import { Tutorial } from 'types/tutorial';
import GeneralItens from 'services/general.service';
import { Step } from '../../types/steps';
import { fetchSteps } from 'services/steps.service';
import { useNavigate } from 'react-router';
import { ControlDate } from 'types/controle-datas';
import { StageIds } from 'utils/constants';
import { fetchControlDatesPublic } from '../../services/controle-datas.service';
import { clearAllCache } from '../../services/cache';

interface AuthContextData {
    login: (credentials: SignInCredentials) => Promise<void | AxiosError>;
    loginWithToken: (access_token: string) => Promise<void>;
    signOut: (pathname?: string) => void;
    // updateNotifications: () => void;
    rule?: RuleEntity;
    refreshUser: () => void;
    onValidateUser: (credentials: ValidateCredentials) => Promise<void | AxiosError>;
    onRegisterUser: (credentials: RegisterCredentials) => Promise<void | AxiosError>;
    user?: User;
    isAuthenticated: boolean;
    loadingUserData: boolean;
    setRule: (val?: any) => void;
    tutorial?: Tutorial;
    setTutorial: (val?: any) => void;
    dates?: [];
    setDates: (val?: any) => void;
    controlDates?: ControlDate[];
    setControlDates: (val?: ControlDate[]) => void;
    loadControlDates: () => Promise<void>;
    appCurrentStep: any;
    setAppCurrentStep: (val?: any) => void;
    notifications: any[];
    setNotifications: (val?: any) => void;
    currentStep?: Step;
    showNotifcation: boolean;
    setShowNotification: (val?: any) => void;
    isBossUser?: boolean;
    stepsList: Step[];
}

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

function AuthProvider({ children }: AuthProviderProps) {
    const navigate = useNavigate();
    const token = getToken();
    const isAuthenticated = Boolean(token);

    const [rule, setRule] = useState<RuleEntity>();
    const [user, setUser] = useState<User | undefined>();
    const [loadingUserData, setLoadingUserData] = useState(true);
    const [tutorial, setTutorial] = useState<Tutorial>();
    const [dates, setDates] = useState<any>([]);
    const [controlDates, setControlDates] = useState<ControlDate[]>([]);
    const [appCurrentStep, setAppCurrentStep] = useState('');
    const [notifications, setNotifications] = useState<any>([]);
    const [currentStep, setCurrentStep] = useState<Step | undefined>();
    const [stepsList, setStepsList] = useState<Step[]>([]);
    // const [notificationsLoaded, setNotificationsLoaded] = useState(false);
    const [showNotifcation, setShowNotification] = useState(false);

    const controlDatesRef = useRef<ControlDate[]>([]);
    useEffect(() => { controlDatesRef.current = controlDates; }, [controlDates]);

    async function getSteps() {
        const response = await fetchSteps();
        setStepsList(response);
    }

    const loadControlDates = useCallback(async () => {
        try {
            const dates = await fetchControlDatesPublic();
            setControlDates(dates);
        } catch (error) {
            console.error('Erro ao carregar datas de controle:', error);
            setControlDates([]);
        }
    }, []);

    const handleSetControlDates = useCallback((val?: ControlDate[]) => {
        setControlDates(val || []);
    }, []);

    useEffect(() => {
        const checkCurrentStep = (steps: Step[]) => {
            const now = Date.now();
            const current = steps.find((step) => now >= step.startDate && now <= step.endDate);

            console.log('current_step_ctx', current);
            setCurrentStep(current);
        };

        if (stepsList.length) {
            checkCurrentStep(stepsList);
        }
    }, [stepsList]);

    // Carrega datas de controle automaticamente ao montar (antes do login)
    useEffect(() => {
        loadControlDates();
    }, [loadControlDates]);

    const signOut = useCallback((pathname?: string) => {
        const target = pathname || '/login';
        removeTokenCookies();
        clearStorage();
        clearAllCache();
        setAuthorizationHeader(api.defaults, '');
        setUser(undefined);
        setLoadingUserData(false);
        navigate(target);
    }, [navigate]);

    // async function updateNotifications() {
    //     if (!user || !user._id || notificationsLoaded) return;
    //     if (currentStep?._id === StageIds.STAGE_INSCRICAO) {
    //         const data = await getNotifications(clearRegistrationUser(user._id));
    //         setNotifications(data);

    //         if (data) {
    //             setShowNotification(true);
    //         }
    //     }
    // }

    const fetchUserDetail = useCallback(async () => {
        if (!user || !user._id) return;

        const token = getToken();
        if (!token) {
            console.error('Token não encontrado ao buscar detalhes do usuário');
            return;
        }

        try {
            setAuthorizationHeader(api.defaults, token);
            const { dates, controlDates: newControlDates, rules, tutorial } = await GeneralItens(controlDatesRef.current);
            await getSteps();

            setRule(rules);
            setTutorial(tutorial);
            setDates(dates);
            setControlDates(newControlDates);
        } catch (error) {
            console.error('Erro ao buscar detalhes do usuário:', error);
        }
    }, [user]);

    const login = useCallback(async ({ username, password }: SignInCredentials) => {
        try {
            const auth = await signInRequest({ username, password });

            if (auth.message) {
                throw auth;
            }

            const { access_token, refreshToken, permissions, roles } = auth;

            createTokenCookies(access_token, refreshToken);
            setAuthorizationHeader(api.defaults, access_token);

            // Aguardar um pouco para garantir que o cookie foi salvo
            await new Promise(resolve => setTimeout(resolve, 100));

            const data = await fetchUser();
            const userData = { _id: data._id, ...data, permissions, roles };

            setUser(userData);

            return userData;
        } catch (error) {
            const err = error as AxiosError;
            console.error('Login error:', err);
            throw err;
        }
    }, []);

    const loginWithToken = useCallback(async (access_token: string) => {
        try {
            if (!access_token) {
                return;
            }

            createTokenCookies(access_token, '');
            setAuthorizationHeader(api.defaults, access_token);

            const data = await fetchUser();

            const { name, image, extra } = data;

            const userData = {
                _id: data._id,
                ...data,
                extra,
                name,
                image,
                permissions: null,
                roles: null
            };

            setUser(userData);

            return userData;
        } catch (error) {
            const err = error as AxiosError;
            throw err;
        }
    }, []);

    async function refreshUser() {
        const updatedUser = await updateUserStatus();
        setUser(updatedUser);
    }

    async function onValidateUser(data: ValidateCredentials) {
        const result = await validateUser(data);
        return result;
    }

    async function onRegisterUser(data: RegisterCredentials) {
        const result = await registerUser(data);
        if (result.status === 'OK') {
            setTimeout(async () => {
                await login({ username: result._id, password: data.password });
            }, 1e3);
        } else {
            throw result;
        }
    }

    // useEffect(() => {
    //     const interval = setInterval(() => {
    //         updateNotifications();
    //     }, 300000);

    //     return () => clearInterval(interval);
    // }, [updateNotifications]);

    useEffect(() => {
        if (!user || !user._id) return;
        
        const token = getToken();
        if (!token) {
            console.warn('User exists but token not found');
            return;
        }
        
        const timeout = setTimeout(() => {
            fetchUserDetail();
        }, 100);
        
        return () => clearTimeout(timeout);
    }, [user, fetchUserDetail]);

    useEffect(() => {
        const token = getToken();

        async function getUserData() {
            setLoadingUserData(true);

            try {
                fetchUser()
                    .then(async (resp) => {
                        if (resp) {
                            const { _id, name, image, email, permissions, roles, detail } = resp;
                            setUser({ ...resp, _id, name, image, email, permissions, roles, detail });
                        }
                    });
            } catch (error) {
                signOut();
            } finally {
            }

            setLoadingUserData(false);
        }

        if (!user && token) {
            setAuthorizationHeader(api.defaults, token);
            getUserData();
        }

        // async function updateNotificationsPeriodically() {
        //     if (user && user._id) {
        //         console.log('Chamando fetchNotifications...');
        //         await updateNotifications();
        //     } else {
        //         console.log('Usuário não definido, não chamando fetchNotifications.');
        //     }
        // }

        // const intervalId = setInterval(updateNotificationsPeriodically, 300000);

        // // Chama uma vez ao montar
        // updateNotificationsPeriodically();

        function handleSignoutEvent() {
            signOut();
        }
        
        // Proteção contra erro de message channel
        try {
            document.addEventListener('signout', handleSignoutEvent, false);
        } catch (error) {
            console.warn('Erro ao adicionar event listener:', error);
        }
        
        return () => {
            try {
                document.removeEventListener('signout', handleSignoutEvent);
            } catch (error) {
                console.warn('Erro ao remover event listener:', error);
            }
        };
    }, [user, signOut]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                loadingUserData,
                login,
                rule,
                setRule,
                loginWithToken,
                signOut,
                // updateNotifications,
                refreshUser,
                onValidateUser,
                onRegisterUser,
                tutorial,
                setTutorial,
                dates,
                setDates,
                controlDates,
                setControlDates: handleSetControlDates,
                loadControlDates,
                currentStep,
                appCurrentStep,
                setAppCurrentStep,
                notifications,
                setNotifications,
                showNotifcation,
                setShowNotification,
                stepsList,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export { AuthProvider, useAuth };
